/**
 * Granny flat arrangement — Centrelink reasonable-value (reasonableness) test + deprivation.
 * Ported verbatim from frontend/src/calculations/grannyFlatArrangement.js.
 *
 * Reasonable value = combined annual partnered Age Pension rate (ON THE DATE the right was
 * established) × conversion factor (age next birthday at establishment; younger of a couple).
 * Deprivation = max(0, total amount paid − value of the granny-flat interest).
 *
 * THE THREE FACE-VALUE FORMS (Social Security Guide 4.6.4.50; Services Australia's worked examples,
 * verified 2 Oct 2026). Transferring the title of the home, paying for the construction of the
 * accommodation, or buying a property in the family member's name — on its own — is accepted as the
 * price of the right: no reasonableness test, no gift, whatever the amount. The test applies when
 * cash (or other assets) is paid, or when anything is given ON TOP of one of those three. Then the
 * right is valued at the greater of what was provided (the home, the build) and the formula, and
 * everything given above that is deprivation. So:
 *   - cash only:               gift = max(0, cash − reasonable value)
 *   - home title only:         gift = 0 (interest = home value)
 *   - construction only:       gift = 0 (interest = max(reasonable, construction) ≥ construction)
 *   - construction + cash:     gift = max(0, construction + cash − max(reasonable, construction))
 *   - home title + cash:       gift = max(0, home + cash − max(reasonable, home)) = cash where home ≥ reasonable
 * `amountPaid` is the CASH paid to the family, exclusive of the construction cost — the fact-find and
 * the module both capture the build separately (`constructionCost`).
 */
import { getGrannyFlatConversionFactor } from './constants/grannyFlatConversionFactors.ts';
import { getPartneredPensionRateAtDate } from './constants/historicalPensionRates.ts';
import { getAustralianFYEndingYearContainingDate } from './giftingDeprivation.ts';
import type { FactFind } from './factFindTypes.ts';

/**
 * Whether a person's residency is a granny-flat arrangement. Supports both the legacy explicit
 * residency value ('granny_flat') and the derived flow: residency 'with_family' + the
 * plain-language answer `livingWithFamily.grannyFlatInterest === true`.
 */
export function isGrannyFlatResidency(residency: string | undefined, factFind: FactFind): boolean {
    if (residency === 'granny_flat') return true;
    return residency === 'with_family' && factFind?.livingWithFamily?.grannyFlatInterest === true;
}

/**
 * Compute the granny-flat deprivation directly from factFind (shared by the engine and the
 * Step 7 read-only display, so both show the same number). Returns null if there's no granny-flat
 * arrangement or no establishment date.
 */
export function getGrannyFlatDeprivationFromFactFind(factFind: FactFind, currentCombinedPartneredRate: number) {
    const gf = factFind?.grannyFlat;
    const isPartnered = factFind?.adviceFor === 'couple';
    const isGf =
        isGrannyFlatResidency(factFind?.client?.currentResidency, factFind) ||
        (isPartnered && isGrannyFlatResidency(factFind?.partner?.currentResidency, factFind));
    if (!isGf || !gf?.dateEntered) return null;
    const dateEntered = gf.dateEntered;

    const ageAtDate = (dob: string | undefined): number | null => {
        if (!dob) return null;
        const b = new Date(dob);
        const a = new Date(dateEntered);
        let age = a.getFullYear() - b.getFullYear();
        if (a.getMonth() < b.getMonth() || (a.getMonth() === b.getMonth() && a.getDate() < b.getDate())) age--;
        return age;
    };
    const clientNb = factFind?.client?.dob ? ageAtDate(factFind.client.dob)! + 1 : null;
    const partnerNb = isPartnered && factFind?.partner?.dob ? ageAtDate(factFind.partner.dob)! + 1 : null;
    const ageNextBirthdayAtEntry =
        isPartnered && partnerNb != null ? Math.min(clientNb ?? Infinity, partnerNb) : clientNb;

    const dep = getGrannyFlatDeprivationAtDate({
        amountPaid: gf.amountPaid ?? 0,
        propertyTransferredValue: gf.propertyTransferredValue ?? 0,
        // The home they lived in is a face-value form; another property or other assets are not
        // (owner, 8 Oct 2026). Older records that never said are the home.
        propertyAtFaceValue: !gf.transferredKind || gf.transferredKind === 'home',
        constructionCost: gf.constructionCost ?? 0,
        ageNextBirthdayAtEntry: ageNextBirthdayAtEntry as number,
        dateEntered,
        currentCombinedPartneredRate,
    });
    return { ...dep, establishmentFyEnd: getAustralianFYEndingYearContainingDate(new Date(dateEntered)) };
}

/** Reasonable value of the granny-flat life interest. */
export function getGrannyFlatReasonableValue({
    ageNextBirthday,
    combinedPartneredPensionRate,
}: {
    ageNextBirthday: number;
    combinedPartneredPensionRate: number;
}): number {
    const factor = getGrannyFlatConversionFactor(ageNextBirthday);
    const rate = combinedPartneredPensionRate || 0;
    return rate * factor;
}

/** Deprivation arising from a granny-flat arrangement (before the gifting free areas are applied). */
export function getGrannyFlatDeprivation({
    amountPaid,
    propertyTransferredValue = 0,
    propertyAtFaceValue = true,
    constructionCost = 0,
    ageNextBirthday,
    combinedPartneredPensionRate,
}: {
    amountPaid: number;
    propertyTransferredValue?: number;
    /**
     * Whether the property transferred is one of the Guide's face-value forms — the home they lived
     * in (or a property bought in the family's name). Another property, or other assets, signed over
     * are "additional assets": counted in what was given, never in what the right can be valued at.
     */
    propertyAtFaceValue?: boolean;
    constructionCost?: number;
    ageNextBirthday: number;
    combinedPartneredPensionRate: number;
}) {
    const cash = amountPaid || 0;
    const property = propertyTransferredValue || 0;
    const construction = constructionCost || 0;

    const conversionFactor = getGrannyFlatConversionFactor(ageNextBirthday);
    const reasonableValue = getGrannyFlatReasonableValue({ ageNextBirthday, combinedPartneredPensionRate });
    // The "consideration received" the person can value up to: the home/construction provided.
    const providedValue = Math.max(propertyAtFaceValue ? property : 0, construction);
    // Value of the granny-flat interest = greater of the reasonableness amount and what was actually provided.
    const interestValue = Math.max(reasonableValue, providedValue);
    // Everything GIVEN for the right (what could be a gift): cash paid + property transferred + the
    // build paid for. Construction on its own can never exceed the interest value (it sets the floor),
    // so it only produces a gift when cash is paid on top — the guide's "additional assets" case.
    const totalPaid = cash + property + construction;
    const deprivation = Math.max(0, totalPaid - interestValue);

    return {
        reasonableValue,
        interestValue,
        totalPaid,
        deprivation,
        ageNextBirthday,
        conversionFactor,
        combinedPartneredPensionRate: combinedPartneredPensionRate || 0,
        providedValue,
    };
}

/**
 * Date-aware granny-flat deprivation (Option B): resolves the combined partnered pension rate AS AT
 * the establishment date from the historical table, then computes the deprivation. Falls back to the
 * current rate (disclosed via `rateApproximated`) when the date is missing or predates the table.
 */
export function getGrannyFlatDeprivationAtDate({
    amountPaid,
    propertyTransferredValue = 0,
    propertyAtFaceValue = true,
    constructionCost = 0,
    ageNextBirthdayAtEntry,
    dateEntered,
    currentCombinedPartneredRate,
}: {
    amountPaid: number;
    propertyTransferredValue?: number;
    propertyAtFaceValue?: boolean;
    constructionCost?: number;
    ageNextBirthdayAtEntry: number;
    dateEntered?: string | Date;
    currentCombinedPartneredRate: number;
}) {
    const atDate = dateEntered ? getPartneredPensionRateAtDate(dateEntered) : null;
    const rateUsed = atDate ? atDate.combinedAnnual : currentCombinedPartneredRate || 0;
    const result = getGrannyFlatDeprivation({
        amountPaid,
        propertyAtFaceValue,
        propertyTransferredValue,
        constructionCost,
        ageNextBirthday: ageNextBirthdayAtEntry,
        combinedPartneredPensionRate: rateUsed,
    });
    return {
        ...result,
        rateUsed,
        rateBasis: atDate ? atDate.basis : 'current-fallback',
        rateApproximated: !atDate,
    };
}
